package link.erpango.quip.ui.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.AlertDialog
import androidx.compose.material.Button
import androidx.compose.material.ButtonDefaults
import androidx.compose.material.Card
import androidx.compose.material.Icon
import androidx.compose.material.IconButton
import androidx.compose.material.LocalContentColor
import androidx.compose.material.LocalTextStyle
import androidx.compose.material.MaterialTheme
import androidx.compose.material.OutlinedButton
import androidx.compose.material.Surface
import androidx.compose.material.Text
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowDropDown
import androidx.compose.material.icons.filled.Clear
import androidx.compose.material.icons.filled.KeyboardArrowDown
import androidx.compose.material.icons.filled.KeyboardArrowUp
import androidx.compose.material.icons.filled.Warning
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.TextButton
import androidx.compose.material3.TextFieldColors
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.luminance
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.toSize
import link.erpango.quip.theme.QuipColors
import link.erpango.quip.theme.QuipDimens
import kotlin.math.min

/**
 * Biblioteca de componentes reutilizables de QUIP
 * Componentes diseñados siguiendo Material Design y mejores prácticas de UI/UX
 */

// ============================================================================
// SECTION HEADER
// ============================================================================

/**
 * Encabezado de sección con icono opcional
 * 
 * @param title Título de la sección
 * @param modifier Modificador opcional
 * @param icon Icono opcional a mostrar antes del título
 */
@Composable
fun QuipSectionHeader(
    title: String,
    modifier: Modifier = Modifier,
    icon: ImageVector? = null
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .padding(vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        if (icon != null) {
            Icon(
                imageVector = icon,
                contentDescription = null,
                tint = QuipColors.colorPrimary,
                modifier = Modifier.size(24.dp)
            )
            Spacer(modifier = Modifier.width(8.dp))
        }
        Text(
            text = title,
            style = MaterialTheme.typography.h6,
            fontWeight = FontWeight.Bold,
            color = QuipColors.colorPrimary
        )
    }
}

// ============================================================================
// CARDS
// ============================================================================

/**
 * Tarjeta de información con elevación y bordes redondeados
 * 
 * @param modifier Modificador opcional
 * @param elevation Elevación de la tarjeta (por defecto 2dp)
 * @param content Contenido de la tarjeta
 */
@Composable
fun QuipCard(
    modifier: Modifier = Modifier,
    elevation: androidx.compose.ui.unit.Dp = 2.dp,
    content: @Composable () -> Unit
) {
    Card(
        modifier = modifier.fillMaxWidth(),
        elevation = elevation,
        shape = RoundedCornerShape(12.dp),
        backgroundColor = Color.White
    ) {
        Box(modifier = Modifier.padding(16.dp)) {
            content()
        }
    }
}

/**
 * Tarjeta de vehículo mejorada con elevación y diseño moderno
 * 
 * @param modifier Modificador opcional
 * @param elevation Elevación de la tarjeta (por defecto 4dp)
 * @param content Contenido de la tarjeta
 */
@Composable
fun QuipVehicleCard(
    modifier: Modifier = Modifier,
    elevation: androidx.compose.ui.unit.Dp = 4.dp,
    content: @Composable () -> Unit
) {
    Card(
        modifier = modifier.fillMaxWidth(),
        elevation = elevation,
        shape = RoundedCornerShape(16.dp),
        backgroundColor = Color.White
    ) {
        Box(modifier = Modifier.padding(20.dp)) {
            content()
        }
    }
}

// ============================================================================
// INFO ROW
// ============================================================================

/**
 * Fila de información con etiqueta y valor
 * 
 * @param label Etiqueta del campo
 * @param value Valor del campo
 * @param icon Icono opcional
 */
@Composable
fun QuipInfoRow(
    label: String,
    value: String,
    icon: ImageVector? = null
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 6.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.weight(1f)
        ) {
            if (icon != null) {
                Icon(
                    imageVector = icon,
                    contentDescription = null,
                    tint = QuipColors.colorText,
                    modifier = Modifier.size(20.dp)
                )
                Spacer(modifier = Modifier.width(8.dp))
            }
            Text(
                text = label,
                style = MaterialTheme.typography.body2,
                color = QuipColors.colorText,
                fontWeight = FontWeight.Medium
            )
        }
        Text(
            text = value,
            style = MaterialTheme.typography.body1,
            color = Color.Black,
            fontWeight = FontWeight.SemiBold,
            textAlign = TextAlign.End,
            modifier = Modifier.weight(1f)
        )
    }
}

// ============================================================================
// BUTTONS
// ============================================================================

/**
 * Botón primario de QUIP con diseño consistente
 * 
 * @param text Texto del botón
 * @param onClick Acción al hacer clic
 * @param modifier Modificador opcional
 * @param icon Icono opcional
 * @param enabled Estado habilitado/deshabilitado
 */
@Composable
fun QuipPrimaryButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    icon: ImageVector? = null,
    enabled: Boolean = true
) {
    Button(
        onClick = onClick,
        modifier = modifier
            .fillMaxWidth()
            .height(56.dp),
        colors = ButtonDefaults.buttonColors(
            backgroundColor = QuipColors.colorPrimary,
            contentColor = QuipColors.colorOnPrimary,
            disabledBackgroundColor = QuipColors.colorPrimary.copy(alpha = 0.5f)
        ),
        shape = RoundedCornerShape(12.dp),
        elevation = ButtonDefaults.elevation(
            defaultElevation = 4.dp,
            pressedElevation = 8.dp
        ),
        enabled = enabled
    ) {
        if (icon != null) {
            Icon(
                imageVector = icon,
                contentDescription = null,
                modifier = Modifier.size(20.dp)
            )
            Spacer(modifier = Modifier.width(8.dp))
        }
        Text(
            text = text,
            style = MaterialTheme.typography.button,
            fontWeight = FontWeight.Bold
        )
    }
}

/**
 * Botón de texto de QUIP
 *
 * @param text Texto del botón
 * @param onClick Acción al hacer clic
 * @param modifier Modificador opcional
 * @param icon Icono opcional
 * @param enabled Estado habilitado/deshabilitado
 */
@Composable
fun QuipTextButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    icon: ImageVector? = null,
    enabled: Boolean = true
) {
    TextButton(
        onClick = onClick,
        modifier = modifier,
        colors = androidx.compose.material3.ButtonDefaults.textButtonColors(
            contentColor = QuipColors.colorPrimary,
            disabledContentColor = QuipColors.colorPrimary.copy(alpha = 0.5f)
        ),
        enabled = enabled
    ) {
        if (icon != null) {
            Icon(
                imageVector = icon,
                contentDescription = null,
                modifier = Modifier.size(20.dp)
            )
            Spacer(modifier = Modifier.width(8.dp))
        }
        Text(
            text = text,
            style = MaterialTheme.typography.button
        )
    }
}

/**
 * Botón secundario de QUIP con borde
 * 
 * @param text Texto del botón
 * @param onClick Acción al hacer clic
 * @param modifier Modificador opcional
 * @param icon Icono opcional
 * @param enabled Estado habilitado/deshabilitado
 */
@Composable
fun QuipSecondaryButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    icon: ImageVector? = null,
    enabled: Boolean = true
) {
    OutlinedButton(
        onClick = onClick,
        modifier = modifier
            .fillMaxWidth()
            .height(56.dp),
        colors = ButtonDefaults.outlinedButtonColors(
            contentColor = QuipColors.colorPrimary
        ),
        shape = RoundedCornerShape(12.dp),
        border = BorderStroke(2.dp, QuipColors.colorPrimary.copy(alpha = if (enabled) 1f else 0.4f)),
        enabled = enabled
    ) {
        if (icon != null) {
            Icon(
                imageVector = icon,
                contentDescription = null,
                modifier = Modifier.size(20.dp)
            )
            Spacer(modifier = Modifier.width(8.dp))
        }
        Text(
            text = text,
            style = MaterialTheme.typography.button,
            fontWeight = FontWeight.Bold
        )
    }
}

// ============================================================================
// TEXT FIELDS
// ============================================================================


@Composable
fun QuipTextFieldDefaults() = OutlinedTextFieldDefaults.colors(
    focusedBorderColor = QuipColors.colorPrimary,
    focusedLabelColor = QuipColors.colorPrimary,
    cursorColor = QuipColors.colorPrimary,
    unfocusedTextColor = QuipColors.colorText,
    unfocusedBorderColor = QuipColors.colorText.copy(alpha = 0.5f),
    unfocusedLabelColor = QuipColors.colorText,
    focusedContainerColor = QuipColors.white,
    unfocusedContainerColor = QuipColors.white,
)

@Composable
fun QuipTextFieldNonDisabled() = OutlinedTextFieldDefaults.colors(
    focusedBorderColor = QuipColors.colorPrimary,
    focusedLabelColor = QuipColors.colorPrimary,
    cursorColor = QuipColors.colorPrimary,
    unfocusedTextColor = QuipColors.colorText,
    unfocusedBorderColor = QuipColors.colorText.copy(alpha = 0.5f),
    unfocusedLabelColor = QuipColors.colorText,
    focusedContainerColor = QuipColors.white,
    unfocusedContainerColor = QuipColors.white,
    disabledBorderColor = QuipColors.colorText.copy(alpha = 0.5f),
)
/**
 * Campo de texto de QUIP con diseño consistente
 *
 * @param value Valor actual del campo
 * @param onValueChange Callback cuando cambia el valor
 * @param label Etiqueta del campo
 * @param modifier Modificador opcional
 * @param icon Icono opcional a mostrar al inicio
 * @param enabled Estado habilitado/deshabilitado
 */
@Composable
fun QuipTextField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    modifier: Modifier = Modifier,
    icon: ImageVector? = null,
    iconModifier: Modifier = Modifier.size(20.dp),
    trailingIcon: @Composable (() -> Unit)? = null,
    placeholder: @Composable (() -> Unit)? = null,
    helperText: @Composable (() -> Unit)? = null,
    enabled: Boolean = true,
    readOnly: Boolean = false,
    isError: Boolean = false,
    keyboardOptions: KeyboardOptions = KeyboardOptions.Default,
    minLines: Int = 1,
    colors: TextFieldColors = QuipTextFieldDefaults()
) {
    Column {
        OutlinedTextField(
            value = value,
            onValueChange = onValueChange,
            label = { Text(label) },
            placeholder = placeholder,
            modifier = modifier
                .fillMaxWidth()
                .testTag("text_field_$label")
                .semantics { contentDescription = "text_field_$label" },
            leadingIcon = if (icon != null) {
                {
                    Icon(
                        imageVector = icon,
                        contentDescription = null,
                        tint = QuipColors.colorPrimary,
                        modifier = iconModifier
                    )
                }
            } else null,
            trailingIcon = trailingIcon,
            colors = colors,
            shape = RoundedCornerShape(12.dp),
            enabled = enabled,
            readOnly = readOnly,
            isError = isError,
            keyboardOptions = keyboardOptions,
            minLines = minLines
        )
        if (helperText != null) {
            helperText()
        }
    }
}

// ============================================================================
// TEXT COMPONENT
// ============================================================================

/**
 * Componente de texto reutilizable de QUIP con soporte para estados enabled/disabled
 *
 * @param text Texto a mostrar
 * @param modifier Modificador opcional
 * @param textStyle Estilo de texto (por defecto body1)
 * @param colorEnabled Color cuando está habilitado
 * @param colorDisabled Color cuando está deshabilitado
 * @param isEnabled Estado habilitado/deshabilitado
 * @param fontWeight Peso de la fuente
 * @param textAlign Alineación del texto
 */
@Composable
fun QuipText(
    text: String,
    modifier: Modifier = Modifier,
    textStyle: TextStyle = MaterialTheme.typography.body1,
    colorEnabled: Color = QuipColors.colorText,
    colorDisabled: Color = QuipColors.colorText.copy(alpha = 0.38f),
    isEnabled: Boolean = true,
    fontWeight: FontWeight? = null,
    textAlign: TextAlign? = null
) {
    Text(
        text = text,
        modifier = modifier,
        style = textStyle,
        color = if (isEnabled) colorEnabled else colorDisabled,
        fontWeight = fontWeight,
        textAlign = textAlign
    )
}

// ============================================================================
// DROPDOWN TEXT FIELD
// ============================================================================

/**
 * Campo de texto con dropdown filtrable de QUIP.
 * Combina un OutlinedTextField con un ExposedDropdownMenu que permite filtrar opciones.
 *
 * @param T Tipo genérico de las opciones
 * @param options Lista de opciones disponibles
 * @param selectedOption Opción actualmente seleccionada (puede ser null)
 * @param itemToStringTitle Función para convertir una opción a texto principal (título)
 * @param itemToStringSubtitle Función opcional para mostrar un subtítulo
 * @param label Etiqueta del campo
 * @param helperText Texto de ayuda debajo del campo
 * @param isError Si hay error de validación
 * @param isEnabled Si el campo está habilitado
 * @param isFiltrable Si se puede filtrar escribiendo en el campo
 * @param onOptionSelected Callback cuando se selecciona una opción (índice, opción)
 * @param onClearOption Callback para limpiar la selección (si es null, no se muestra botón de limpiar)
 * @param modifier Modificador opcional
 * @param onlyNumber Si solo acepta números
 * @param textFieldColors Colores del campo de texto
 * @param helperTextFocusedColor Color del helper text cuando está enfocado
 * @param helperTextUnfocusedColor Color del helper text cuando no está enfocado
 * @param helperTextErrorColor Color del helper text cuando hay error
 * @param onTextChanged Callback cuando cambia el texto (para búsquedas externas)
 * @param maxCharacters Número máximo de caracteres permitidos
 * @param keyboardOptions Opciones del teclado
 * @param keyboardActions Acciones del teclado
 */
@Composable
fun <T> QuipDropdownTextField(
    options: List<T>,
    selectedOption: T?,
    itemToStringTitle: ((T) -> String)? = null,
    itemToStringSubtitle: ((T) -> String)? = null,
    label: String,
    helperText: String? = null,
    isError: Boolean = false,
    isEnabled: Boolean = true,
    isFiltrable: Boolean = false,
    onOptionSelected: (Int, T) -> Unit,
    onClearOption: (() -> Unit)? = null,
    modifier: Modifier = Modifier,
    onlyNumber: Boolean = false,
    textFieldColors: TextFieldColors = QuipDropdownTextFieldDefaults(isEnabled = isEnabled),
    helperTextFocusedColor: Color = QuipColors.colorPrimary,
    helperTextUnfocusedColor: Color = QuipColors.colorText,
    helperTextErrorColor: Color = QuipColors.colorError,
    enabledClearButtonColor: Color = QuipColors.colorText,
    disabledClearButtonColor: Color = QuipColors.colorText.copy(alpha = 0.38f),
    enabledDropdownButtonColor: Color = QuipColors.colorText,
    disabledDropdownButtonColor: Color = QuipColors.colorText.copy(alpha = 0.38f),
    onTextChanged: ((String) -> Unit)? = null,
    maxCharacters: Int? = null,
    keyboardOptions: KeyboardOptions = KeyboardOptions.Default.copy(imeAction = ImeAction.Done),
    keyboardActions: KeyboardActions? = null,
    hasToIgnoreFocusToggleOnDismissActions: Boolean = false
) {
    var isFocused by remember { mutableStateOf(false) }
    var expandedState by remember { mutableStateOf(false) }
    val focusManager = LocalFocusManager.current
    val isStringType = selectedOption is String
    val userTextInput = remember { mutableStateOf(TextFieldValue(text = "", selection = TextRange(0))) }
    val textFieldSize = remember { mutableStateOf(Size.Zero) }

    LaunchedEffect(selectedOption) {
        if (selectedOption != null) {
            val newValue = itemToStringTitle?.invoke(selectedOption) ?: selectedOption.toString()
            // Only update if text actually changed to avoid resetting cursor position
            // during character-by-character editing
            if (userTextInput.value.text != newValue) {
                userTextInput.value = TextFieldValue(
                    text = newValue,
                    selection = TextRange(newValue.length)
                )
            }
        } else {
            // When selectedOption becomes null (e.g. parent cleared it),
            // only clear if the field is not currently being edited by the user.
            // During backspace editing, onValueChange already maintains userTextInput correctly.
            // This handles the case where external code sets selectedOption to null
            // without going through the text field (e.g. resetting a form).
            if (!isFiltrable) {
                userTextInput.value = TextFieldValue(text = "", selection = TextRange(0))
            }
        }
    }

    val query = userTextInput.value.text.trim()
    val filteredOptions: List<T> = remember(query, options) {
        if (!isFiltrable || query.isBlank()) options
        else options.filter {
            val value = itemToStringTitle?.invoke(it) ?: it.toString()
            value.contains(query, ignoreCase = true)
        }
    }

    var ignoreNextOnExpandedChange = false

    val customKeyboardActions = keyboardActions ?: KeyboardActions(
        onDone = {
            ignoreNextOnExpandedChange = true
            isFocused = false
            expandedState = false
            focusManager.clearFocus(true)
        },
        onNext = {
            ignoreNextOnExpandedChange = true
            isFocused = false
            expandedState = false
            focusManager.clearFocus(true)
        }
    )

    Column(modifier = modifier) {
        QuipDropdownMenuContainer(
            expanded = expandedState,
            onExpandedChange = { 
                if (isEnabled && (!ignoreNextOnExpandedChange || !hasToIgnoreFocusToggleOnDismissActions)) {
                    expandedState = it
                    ignoreNextOnExpandedChange = false
                }
            },
            modifier = Modifier.fillMaxWidth(),
            textField = { modifier ->
                OutlinedTextField(
                    value = userTextInput.value,
                    textStyle = MaterialTheme.typography.body1,
                    onValueChange = { input ->
                        var filteredInput = input.text
                        if (onlyNumber) {
                            filteredInput = filteredInput.filter { it.isDigit() }
                        }
                        if (maxCharacters != null && filteredInput.length > maxCharacters) {
                            filteredInput = filteredInput.take(maxCharacters)
                        }
                        userTextInput.value = input.copy(
                            text = filteredInput,
                            selection = input.selection,
                            composition = input.composition
                        )

                        if (selectedOption != null) {
                            val selectedText = itemToStringTitle?.invoke(selectedOption) ?: selectedOption.toString()
                            if (filteredInput != selectedText) {
                                onClearOption?.invoke()
                            }
                        }
                        onTextChanged?.invoke(filteredInput)
                        expandedState = true
                    },
                    modifier = modifier
                        .fillMaxWidth()
                        .testTag("dropdown_field_$label")
                        .semantics { contentDescription = "dropdown_field_$label" }
                        .onFocusChanged { focusState ->
                            isFocused = focusState.isFocused
                            // Delay closing to allow click events to propagate
                            if (!focusState.isFocused) {
                               // We don't immediately close here to let DropdownMenuItem click happen
                               // The DropdownMenu's onDismissRequest will handle closing
                            }
                        }
                        .onGloballyPositioned { textFieldSize.value = it.size.toSize() },
                    singleLine = true,
                    keyboardOptions = keyboardOptions,
                    keyboardActions = customKeyboardActions,
                    readOnly = !isFiltrable,
                    enabled = isEnabled,
                    label = { 
                        QuipText(
                            text = label, 
                            colorEnabled = LocalContentColor.current, 
                            textStyle = LocalTextStyle.current
                        ) 
                    },
                    trailingIcon = {
                        if (isError) {
                            Icon(
                                imageVector = Icons.Default.Warning,
                                contentDescription = "Error",
                                tint = QuipColors.colorError,
                                modifier = Modifier.size(QuipDimens.iconSize20)
                            )
                        } else {
                            Row {
                                // Botón de limpiar
                                if (onClearOption != null &&
                                    (selectedOption != null &&
                                            (isStringType && selectedOption.toString().isNotEmpty() ||
                                                    !isStringType && itemToStringTitle?.invoke(
                                                selectedOption
                                            )?.isNotEmpty() == true) ||
                                            userTextInput.value.text.isNotEmpty()
                                            ) && isEnabled
                                ) {
                                    IconButton(
                                        onClick = {
                                            userTextInput.value = TextFieldValue("")
                                            expandedState = false
                                            onClearOption.invoke()
                                            focusManager.clearFocus()
                                        },
                                        modifier = Modifier.testTag("dropdown_clear_$label")
                                    ) {
                                        Icon(
                                            imageVector = Icons.Default.Clear,
                                            contentDescription = "Limpiar $label",
                                            tint = if (isEnabled) enabledClearButtonColor else disabledClearButtonColor,
                                            modifier = Modifier.size(QuipDimens.iconSize20)
                                        )
                                    }
                                }
                                // Botón de dropdown (Toggle)
                                IconButton(
                                    onClick = { 
                                        if (isEnabled) {
                                            expandedState = !expandedState
                                            if (expandedState) {
                                                focusManager.clearFocus() // Optional: depends on UX preference
                                            }
                                        }
                                    },
                                    modifier = Modifier.testTag("dropdown_toggle_$label")
                                ) {
                                    Icon(
                                        imageVector = if (expandedState) Icons.Default.KeyboardArrowUp else Icons.Default.KeyboardArrowDown,
                                        contentDescription = if (expandedState) "Cerrar $label" else "Abrir $label",
                                        tint = if (isEnabled) enabledDropdownButtonColor else disabledDropdownButtonColor,
                                        modifier = Modifier.size(QuipDimens.iconSize20)
                                    )
                                }
                            }
                        }
                    },
                    colors = textFieldColors,
                    isError = isError,
                    shape = RoundedCornerShape(QuipDimens.cornerRadius12)
                )
            },
            dropdownContent = {
                val itemsToShow = if (isFiltrable) filteredOptions else options
                if (itemsToShow.isNotEmpty()) {
                     itemsToShow.forEachIndexed { index, option ->
                        val newText = itemToStringTitle?.invoke(option) ?: option.toString()
                        
                         DropdownMenuItem(
                            text = {
                                Column(modifier = Modifier.padding(vertical = QuipDimens.padding10)) {
                                    QuipText(
                                        text = newText
                                    )
                                    val subtitle = itemToStringSubtitle?.invoke(option)
                                    if (!subtitle.isNullOrBlank()) {
                                        QuipText(
                                            text = subtitle,
                                            textStyle = MaterialTheme.typography.caption,
                                            colorEnabled = QuipColors.colorTextSecondary
                                        )
                                    }
                                }
                            },
                            onClick = {
                                userTextInput.value = TextFieldValue(
                                    text = newText,
                                    selection = TextRange(newText.length)
                                )
                                val originalIndex = options.indexOf(option)
                                expandedState = false
                                onOptionSelected(originalIndex, option)
                                focusManager.clearFocus() 
                            }
                        )
                    }
                }
            }
        )
        
        // Helper text
        helperText?.let {
            val helperColor = when {
                isError -> helperTextErrorColor
                isFocused -> helperTextFocusedColor
                else -> helperTextUnfocusedColor
            }
            QuipText(
                text = it,
                colorEnabled = helperColor,
                colorDisabled = QuipColors.colorText.copy(alpha = 0.38f),
                isEnabled = isEnabled,
                textStyle = MaterialTheme.typography.caption,
                modifier = Modifier.padding(top = QuipDimens.padding4, start = QuipDimens.padding16)
            )
        }
    }
}

/**
 * Colores por defecto para QuipDropdownTextField
 */
@Composable
fun QuipDropdownTextFieldDefaults(isEnabled: Boolean = true) = OutlinedTextFieldDefaults.colors(
    focusedBorderColor = if (isEnabled) QuipColors.colorPrimary else QuipColors.colorText.copy(alpha = 0.38f),
    focusedLabelColor = if (isEnabled) QuipColors.colorPrimary else QuipColors.colorText.copy(alpha = 0.38f),
    unfocusedBorderColor = QuipColors.colorText.copy(alpha = 0.5f),
    unfocusedLabelColor = QuipColors.colorText,
    disabledTextColor = QuipColors.colorText.copy(alpha = 0.38f),
    disabledBorderColor = QuipColors.colorText.copy(alpha = 0.38f),
    disabledLabelColor = QuipColors.colorText.copy(alpha = 0.38f),
    disabledTrailingIconColor = QuipColors.colorText.copy(alpha = 0.38f),
    cursorColor = QuipColors.colorText,
    focusedTextColor = if (isEnabled) QuipColors.colorText else QuipColors.colorText.copy(alpha = 0.38f),
    unfocusedTextColor = if (isEnabled) QuipColors.colorText else QuipColors.colorText.copy(alpha = 0.38f),
    focusedContainerColor = QuipColors.white,
    unfocusedContainerColor = QuipColors.white,
    disabledContainerColor = QuipColors.white,
    errorBorderColor = QuipColors.colorError,
    errorLabelColor = QuipColors.colorError,
    errorCursorColor = QuipColors.colorError,
)

// ============================================================================
// BADGES & INDICATORS
// ============================================================================

/**
 * Badge circular con icono
 * 
 * @param icon Icono a mostrar
 * @param backgroundColor Color de fondo
 * @param iconTint Color del icono
 * @param size Tamaño del badge
 */
@Composable
fun QuipIconBadge(
    icon: ImageVector,
    modifier: Modifier = Modifier,
    backgroundColor: Color = QuipColors.colorPrimary.copy(alpha = 0.1f),
    iconTint: Color = QuipColors.colorPrimary,
    size: androidx.compose.ui.unit.Dp = 48.dp,
    iconSize: androidx.compose.ui.unit.Dp = 28.dp
) {
    Surface(
        modifier = modifier.size(size),
        shape = CircleShape,
        color = backgroundColor
    ) {
        Box(contentAlignment = Alignment.Center) {
            Icon(
                imageVector = icon,
                contentDescription = null,
                tint = iconTint,
                modifier = Modifier.size(iconSize)
            )
        }
    }
}

/**
 * Badge de texto con fondo redondeado
 * 
 * @param text Texto a mostrar
 * @param backgroundColor Color de fondo
 * @param textColor Color del texto
 */
@Composable
fun QuipTextBadge(
    text: String,
    modifier: Modifier = Modifier,
    backgroundColor: Color = QuipColors.colorPrimary.copy(alpha = 0.15f),
    textColor: Color = QuipColors.colorPrimary
) {
    Surface(
        modifier = modifier,
        shape = RoundedCornerShape(8.dp),
        color = backgroundColor
    ) {
        Text(
            text = text,
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
            style = MaterialTheme.typography.caption,
            fontWeight = FontWeight.Bold,
            color = textColor
        )
    }
}

// ============================================================================
// DIVIDERS
// ============================================================================

/**
 * Divider con texto en el medio
 * 
 * @param text Texto a mostrar en el divider
 */
@Composable
fun QuipDividerWithText(
    text: String,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically
    ) {
        androidx.compose.material.Divider(
            modifier = Modifier.weight(1f),
            color = QuipColors.colorText.copy(alpha = 0.3f)
        )
        Text(
            text = "  $text  ",
            style = MaterialTheme.typography.caption,
            color = QuipColors.colorText
        )
        androidx.compose.material.Divider(
            modifier = Modifier.weight(1f),
            color = QuipColors.colorText.copy(alpha = 0.3f)
        )
    }
}

// ============================================================================
// DIALOGS
// ============================================================================

/**
 * Diálogo genérico de QUIP con diseño estandarizado.
 * 
 * @param title Título del diálogo
 * @param message Mensaje/contenido del diálogo
 * @param confirmButtonText Texto del botón de confirmación
 * @param dismissButtonText Texto del botón de cancelación
 * @param onConfirm Acción al confirmar
 * @param onDismiss Acción al cancelar/cerrar
 * @param isConfirmColored Si es true, el botón de confirmación tiene fondo de color.
 *                         Si es false, el botón de cancelación tiene fondo de color.
 * @param accentColor Color de fondo del botón destacado (por defecto colorPrimary)
 */
@Composable
fun QuipDialogGeneric(
    title: String,
    message: String,
    confirmButtonText: String,
    dismissButtonText: String,
    onConfirm: () -> Unit,
    onDismiss: () -> Unit,
    isConfirmColored: Boolean = true,
    accentColor: Color = QuipColors.colorPrimary
) {
    QuipDialogGeneric(
        title = title,
        content = {
            Text(
                text = message,
                color = QuipColors.colorTextSecondary
            )
        },
        confirmButtonText = confirmButtonText,
        dismissButtonText = dismissButtonText,
        onConfirm = onConfirm,
        onDismiss = onDismiss,
        isConfirmColored = isConfirmColored,
        accentColor = accentColor
    )
}

@Composable
fun QuipDialogGeneric(
    title: String,
    content: @Composable () -> Unit,
    confirmButtonText: String,
    dismissButtonText: String,
    onConfirm: () -> Unit,
    onDismiss: () -> Unit,
    isConfirmColored: Boolean = true,
    accentColor: Color = QuipColors.colorPrimary
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Text(
                text = title,
                color = QuipColors.colorText,
                fontWeight = FontWeight.Bold
            )
        },
        text = content,
        confirmButton = {
            if (isConfirmColored) {
                Button(
                    onClick = onConfirm,
                    colors = ButtonDefaults.buttonColors(
                        backgroundColor = accentColor,
                        contentColor = contrastColorFor(accentColor)
                    ),
                    shape = RoundedCornerShape(8.dp)
                ) {
                    Text(text = confirmButtonText, fontWeight = FontWeight.Medium)
                }
            } else {
                TextButton(onClick = onConfirm) {
                    Text(
                        text = confirmButtonText,
                        color = QuipColors.colorText,
                        fontWeight = FontWeight.Medium
                    )
                }
            }
        },
        dismissButton = {
            if (!isConfirmColored) {
                Button(
                    onClick = onDismiss,
                    colors = ButtonDefaults.buttonColors(
                        backgroundColor = accentColor,
                        contentColor = contrastColorFor(accentColor)
                    ),
                    shape = RoundedCornerShape(8.dp)
                ) {
                    Text(text = dismissButtonText, fontWeight = FontWeight.Medium)
                }
            } else {
                TextButton(onClick = onDismiss) {
                    Text(
                        text = dismissButtonText,
                        color = QuipColors.colorText,
                        fontWeight = FontWeight.Medium
                    )
                }
            }
        },
        backgroundColor = Color.White,
        shape = RoundedCornerShape(16.dp)
    )
}

/**
 * Calcula el color de contraste (blanco o negro) para un color de fondo dado.
 * Usa la luminancia del color para determinar si el texto debe ser claro u oscuro.
 */
@Composable
private fun contrastColorFor(backgroundColor: Color): Color {
    return if (backgroundColor.luminance() > 0.5f) {
        QuipColors.black
    } else {
        QuipColors.white
    }
}
